import {LitElement, html, css, unsafeSVG} from "../libs/lit.dist.js";
import {getGlobalStyleSheets} from "./abstract-element.js";
import EMOJI_LIST from '../icons-emoji.js';

let isEmojiFontsInitialized = false;
const initializeEmojiFonts = () => {
    if (!isEmojiFontsInitialized) {

        const fontApisLink = document.createElement('link');
        fontApisLink.rel = 'preconnect';
        fontApisLink.href = 'https://fonts.googleapis.com';
        document.head.appendChild(fontApisLink);

        const fontApisLinkCors = document.createElement('link');
        fontApisLinkCors.rel = 'preconnect';
        fontApisLinkCors.href = 'https://fonts.googleapis.com';
        fontApisLinkCors.crossorigin = 'true';
        document.head.appendChild(fontApisLinkCors);

        const fontLink = document.createElement('link');
        fontApisLinkCors.rel = 'stylesheet';
        fontApisLinkCors.href = 'https://fonts.googleapis.com/css2?family=Noto+Color+Emoji&family=Noto+Emoji:wght@700&display=swap';
        document.head.appendChild(fontApisLinkCors);

        isEmojiFontsInitialized = true;
    }
};

/**
 * A custom element for displaying icons using different icon collections.
 * `PkIcon` extends LitElement and provides flexibility to render icons from
 * various icon collections including FontAwesome, Bootstrap icons, and custom icon collections.
 *
 * Key Features:
 * - Supports multiple icon collections such as FontAwesome, Bootstrap, and custom collections.
 * - Offers customization for icon type (e.g., regular, solid, light, dual tone).
 * - Dynamically computes and applies CSS classes for proper rendering of icons.
 * - Encapsulates styles and ensures ease of integration with various projects.
 *
 * Properties:
 * - `icon` (String): The name of the icon to be displayed. Automatically prefixes the icon name based on the specified collection.
 * - `type` (String): Specifies the type of icon to use within the collection. For FontAwesome, valid types include "regular", "solid", "light", and "dual tone".
 * - `collection` (String): The icon collection to be used. Supported options include:
 *    - "fa": Font Awesome (Free)
 *    - "bi": Bootstrap Icons
 *    - "pk": Custom icon name prefix "pk-icon-"
 *    - "ei" | "emoji": Emoji color (Noto Color Emoji) @see https://fonts.google.com/noto/specimen/Noto+Color+Emoji?query=emoji
 *    - "em" | "emoji-mono": Emoji mono (Noto Emoji) @see https://fonts.google.com/noto/specimen/Noto+Emoji?query=emoji
 *    - "svg": SVG icon from our CDN (https://cdn.polkurier.pl/images/icons/*.svg)
 *    - Default: FontAwesome with applicable subtypes.
 * - `size`: (string) The size of the icon. Supported values include:
 *    - "text": Container text size
 *    - "xs": Extra small (1rem)
 *    - "sm": Small (1.5rem)
 *    - "md": Medium (2rem)
 *    - "lg": Large (3rem)
 *    - "xl": Extra large (4rem)
 *    - "auto": Auto-size based on the icon size.
 *    - Default: "md"
 * - `decoration`: (string) The decoration to be applied to the icon. Supported values include:
 *    - "circle": Circle decoration
 *    - Default: None
 * - `fixedWidth`: (boolean) Whether to apply the "fa-fw" class to the icon.
 *
 * Usage:
 *
 * <pk-icon icon="users"></pk-icon>
 * <pk-icon collection=svg icon="surprise-box" fancy></pk-icon>
 *
 */
export class PkIcon extends LitElement {

    static PRIMARY_COLOR_ATTRIBUTE = 'data-color-primary';
    static SECONDARY_COLOR_ATTRIBUTE = 'data-color-secondary';
    static ACCENT_COLOR_ATTRIBUTE = 'data-color-accent';

    static PRIMARY_COLOR_DEFAULT_REGEXP = '#(0{3,8})';
    static SECONDARY_COLOR_DEFAULT_REGEXP = '#dcdcdc';
    static ACCENT_COLOR_DEFAULT_REGEXP = '#d21a1d|#D21317|#d6171a|7f7f7';

    static svgCache = {};

    static get styles() {
        return [
            getGlobalStyleSheets(),
            css`
                :host {
                    --__pk-icon-color: var(--pk-icon-color, currentColor);
                    --__pk-icon-bg: var(--pk-icon-bg, var(--pk-icon-bg, hsl(from var(--__pk-icon-color) h s 92% / 0.7)));
                    --__pk-icon-size: var(--pk-icon-size, 6rem);
                    --pk-icon-color-accent: #d21a1d;
                    --pk-icon-color-secondary: #f2f2f2;
                    color: var(--__pk-icon-color);
                    text-align: center;
                    display: inline-block;
                }

                i {
                    display: inline-block;
                }
                
                .ei,
                .em {
                    font-style: normal;
                    display: inline-block;
                    font-variant: normal;
                    text-transform: none;
                    line-height: 1;
                    vertical-align: -0.125em;
                    -webkit-font-smoothing: antialiased;
                    font-weight: 400 !important;
                    font-family: "Noto Color Emoji", sans-serif;
                    user-select: none;
                }

                .em {
                    font-family: "Noto Emoji", sans-serif;
                    font-weight: 700 !important;
                }

                .svg {
                    display: inline-block;
                    width: var(--pk-icon-size, 2ch);
                    svg {
                        width: 100%;
                        height: auto;
                        display: inline-block;
                    }
                }

                :host([decoration="circle"]) {
                    --pk-icon-color-accent: hsl(from var(--__pk-icon-color) h s 25%);
                    position: relative;
                    display: inline-block;
                    width: var(--__pk-icon-size);
                    height: var(--__pk-icon-size);

                    &::before {
                        content: '';
                        position: absolute;
                        top: 0;
                        left: 0;
                        border-radius: 50%;
                        background-color: var(--__pk-icon-bg);
                        width: calc(var(--__pk-icon-size) * 0.75);
                        height: calc(var(--__pk-icon-size) * 0.75);
                        z-index: 5;
                    }

                    i {
                        position: absolute;
                        z-index: 10;
                        top: 50%;
                        left: 50%;
                        translate: -50% -50%;
                        font-size: calc(var(--__pk-icon-size) * 0.75);
                    }
                }

                :host([size="text"]) {
                    --pk-icon-size: 2ch;
                }

                :host([size="xs"]) {
                    --pk-icon-size: 1rem;
                }

                :host([size="sm"]) {
                    --pk-icon-size: 2rem;
                }

                :host([size="md"]) {
                    --pk-icon-size: 3rem;
                }

                :host([size="lg"]) {
                    --pk-icon-size: 4rem;
                }

                :host([size="xl"]) {
                    --pk-icon-size: 5rem;
                }

                :host([size="auto"]) {
                    --pk-icon-size: 100%;
                }
            `,
        ];
    }

    static properties = {
        icon: {type: String},
        type: {type: String},
        alt: {type: String},
        collection: {type: String},
        decoration: {type: String, reflect: true},
        fixedWidth: {type: Boolean},
    }

    constructor(props) {
        super(props);
    }

    connectedCallback() {
        super.connectedCallback();
        this.role = '';
        this.alt = '';
    }

    get #innerCssClasses() {
        const classes = [];
        let prefix;
        switch (this.collection) {
            case 'bi':
                prefix = 'bi-';
                classes.push('bi');
                break;

            case 'pk':
                prefix = 'pk-icon-';
                classes.push('pk');
                break;

            case 'ei':
            case 'emoji':
                prefix = 'ei-';
                classes.push('ei');
                break;

            case 'em':
            case 'emoji-mono':
                prefix = 'em-';
                classes.push('em');
                break;

            case 'svg':
                prefix = 'svg-';
                classes.push('svg');
                break;

            default:
                prefix = 'fa-';
                switch (this.type) {
                    case 'regular':
                    case 'far':
                        classes.push('far');
                        break;

                    case 'light':
                    case 'fal':
                        classes.push('fal');
                        break;

                    case 'dual':
                    case 'dual_tone':
                    case 'dualTone':
                    case 'fad':
                        classes.push('fad');
                        break;

                    case 'solid':
                    case 'fas':
                        classes.push('fas');
                        break;

                    default:
                        classes.push('fa');
                }
                break;
        }

        if (this.fixedWidth) {
            classes.push('fa-fw');
        }

        let icon = String(this.icon).trim();
        if (icon.indexOf(prefix) !== 0) {
            icon = prefix + this.icon;
        }
        if (icon) {
            classes.push(icon);
        }
        return classes;
    }

    update(t) {
        super.update(t);

        if (t.has('collection') && this.#isEmoji) {
            initializeEmojiFonts();
        }

        if ((t.has('collection') || t.has('icon')) && this.collection === 'svg') {
            this.#loadSvg().then();
        }

        this.ariaHidden = this.alt === '';
    }

    async #loadSvg() {
        if (this.constructor.svgCache[this.icon]) {
            if (this.constructor.svgCache[this.icon] instanceof Promise) {
                this.constructor.svgCache[this.icon].finally(() => this.requestUpdate());
            }
            return;
        }

        const promise = this.constructor.svgCache[this.icon] = new Promise(async (resolve) => {
            const response = await fetch(POLKURIER.paths.get('cdn_url') + `/images/icons/${this.icon}.svg`);
            const contentType = response.headers.get('content-type');
            if (contentType === 'image/svg+xml' || contentType === 'image/svg') {
                this.constructor.svgCache[this.icon] = await response.text();
            } else {
                this.constructor.svgCache[this.icon] = '<svg></svg>'
            }
            resolve();
        });
        promise.finally(() => this.requestUpdate());
    }

    get #isEmoji() {
        return this.collection === 'emoji' || this.collection === 'ei' || this.collection === 'emoji-mono' || this.collection === 'em';
    }

    static getAttributeValueFromString(string, attributeName) {
        if (!string.includes(attributeName)) {
            return;
        }
        const attribute = string.substring(string.indexOf(attributeName));
        let attributeValue = attribute.substring(attribute.indexOf('"') + 1);
        return attributeValue.substring(0, attributeValue.indexOf('"'));
    }

    get #iconContent() {
        if (this.collection === 'svg' && typeof this.constructor.svgCache[this.icon] === 'string') {
            let content = this.constructor.svgCache[this.icon];
            if (content && this.type !== 'color') {
                const cacheKey = this.icon + '--parametrized';
                if (!this.constructor.svgCache[cacheKey]) {
                    const primaryColor = this.constructor.getAttributeValueFromString(content, this.constructor.PRIMARY_COLOR_ATTRIBUTE);
                    const secondaryColor = this.constructor.getAttributeValueFromString(content, this.constructor.SECONDARY_COLOR_ATTRIBUTE);
                    const accentColor = this.constructor.getAttributeValueFromString(content, this.constructor.ACCENT_COLOR_ATTRIBUTE);
                    this.constructor.svgCache[cacheKey] = content
                        .replace(new RegExp(primaryColor || this.constructor.PRIMARY_COLOR_DEFAULT_REGEXP, 'gim'), 'currentcolor')
                        .replace(new RegExp(secondaryColor || this.constructor.SECONDARY_COLOR_DEFAULT_REGEXP, 'gim'), `var(--pk-icon-color-secondary)`)
                        .replace(new RegExp(accentColor || this.constructor.ACCENT_COLOR_DEFAULT_REGEXP, 'gim'), `var(--pk-icon-color-accent)`);
                }
                content = this.constructor.svgCache[cacheKey];
            }
            return unsafeSVG(content);
        }

        if (this.#isEmoji) {
            return EMOJI_LIST[this.icon] || this.icon;
        }

        return null;
    }

    render() {
        return html`<i class="${this.#innerCssClasses.join(' ')}">${this.#iconContent}</i>`;
    }
}

if (!customElements.get('pk-icon')) {
    customElements.define('pk-icon', PkIcon);
}

export class PkSvgIcon extends PkIcon {
    constructor() {
        super();
        this.collection = 'svg';
        this.decoration = 'circle';
    }
}

if (!customElements.get('pk-svg-icon')) {
    customElements.define('pk-svg-icon', PkSvgIcon);
}
